<template>
    <div id = 'blood-draw-view-container' v-loading="page_loading">
        <div v-if="non_blood_draw_needed" class="back-to-collections-btn" @click="goToCollections">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 13L4 8L10 3" stroke="#0E4672" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Back to Sample Collection</span>
        </div>
        <SwitchTabs :tabs="tabs" v-model="active_tab"/>
  
        <div v-if = "locations_shown!=null && locations_shown.length!=0" class = 'locations-info-wrapper'>
            <div class = "all-location-units" ref = 'all-location-units'>
                <LocationUnit v-for = "(location,index) in locations_shown" :key = "`location_unit-${location?.location_name?.replaceAll(' ','_')??index}`"
                v-bind="location" :index = "index"
                @select="(cur_selected_index)=>selectNewLocationUnit(cur_selected_index)"
                :ref = "`location-unit-${index}`"/>
            </div>
            <div class = 'map-div'>
                <EmbededGoogleMap />
            </div>
        </div>
        <div v-else class = 'no-locations-notice-wrapper'>
            <div class = "no-locations-notice">
                Your provider does not have a preferred draw site. 
                <br />
                Please use <span class = "bold">"Go to map search"</span> button below to search for a draw site close to you.
            </div>
        </div>
        <div class = "nav-to-new-ops-bar">
            <div :class = "[
                'go-to-map-search-div',
                'full-width'
            ]">
                <div class = 'guidance-info-and-ops-wrapper'>
                    <div class = "guidance-title">
                        Need more options?
                    </div>
                    <div class = "guidance-text">
                        If these options don't work for you, please click here for all draw site options near your location.
                    </div>
                    <div class = "go-to-map-search-btn" @click = "goToMapSearch()">
                        <div>Go to map search</div>
                        <img src = "@/assets/BloodDraw/nav_to_new_icon.svg" class = "nav-to-new-icon"/>
                    </div>
                </div>
            
                <img src="@/assets/BloodDraw/go_to_map_pic.svg" class = "go-to-map-search-pic"/>
            </div>
        </div>
    </div>
</template>

<script>
import SwitchTabs from "./SwitchTabs.vue";
import LocationUnit from "./LocationUnit.vue";
import EmbededGoogleMap from "./EmbededGoogleMap.vue";
import { mapActions, mapState } from "pinia";
import { useBloodDrawStore } from '@/stores/BloodDrawStore.ts';
import { useCommonStore } from "@/stores/CommonStore.ts";
import { ElNotification } from "element-plus";
export default{
    components:{
        SwitchTabs,
        LocationUnit,
        EmbededGoogleMap,
    },
    setup(){
        const bloodDrawStore = useBloodDrawStore();
        return{
            bloodDrawStore,
        }
    },
    data(){
        return{
            tabs:['At-Home Blood Draw', 'Blood Draw Site'],
            active_tab:'',
            location_test:{
                location_name: 'Mills Health Center Lab Satellite Draw Station',
                open_hour: 'Mon - Fri 8:00 - 17:00',
                address:' 1936 Camden Ave Suite 4A, San Jose CA 95124',
                service_tags:['Walk In', 'Appointment']
            },
            window_size:'',
            selected_location_unit_index:0,
        }
    },
    async mounted(){
        //start loading
        this.bloodDrawStore.page_loading = true;
        try{
            //if token, sample_id or accession_id in BloodDrawStore are null or empty, means user refresh at the current page:
            // -> need to get the query from the localStorage, and reset variables in store
            if(!this.sample_id || !this.accession_id || !this.token){
                // 1. set data in BloodDrawStore: for page initiation when refresh at /bloodDrawLanding
                // NOTES: need to use await, since storeAllQueryInfomation might throw Error
                await this.storeAllQueryInfomation(JSON.parse(localStorage.getItem('pns_single_page_query')));
                
                // 2. set data in CommonStore: for the top header information and 'pnsToken' in localStorage
                this.setAccessionId(this.accession_id);
                this.setSampleId(this.sample_id);  
                this.setJwtToken(this.token);
            }
            // 3. call the initiateBloodDrawSitePage() to fetch all information needed
            await this.initiateBloodDrawSitePage();

            //set tabs (handle tabs_order == null, when this customer has no pns setting)
            this.tabs = this.blood_draw_data==null || this.blood_draw_data.tabs_order==null || this.blood_draw_data.tabs_order[0] == 'mobile'?['At-Home Blood Draw', 'Blood Draw Site']:['Blood Draw Site', 'At-Home Blood Draw']
            this.active_tab = this.tabs[0];
            this.setCurActiveTab(this.active_tab);
            //default select the first location unit
            this.selected_location_unit_index = 0;
        }catch(err){
            console.log('BloodDrawView mounted error: ',err);
            ElNotification({
                title: 'Failed',
                type: 'error',
                customClass: "errorNotification",
                message: err?.message ?? 'BloodDrawView mounted error.',
                duration: 0,
            });
            this.$router.push('/failed');
        }finally{
            //stop loading
            this.bloodDrawStore.page_loading = false;
        }
    },
    computed:{
        // ...mapState(()=>useCommonStore(), ["sample_id", "accession_id"]),
        ...mapState(()=>useBloodDrawStore(),[
            'accession_id',
            'sample_id',
            'token',
            'patient_id',
            "from_source",
            'blood_draw_data',
            'non_blood_draw_needed',
            'collection_page_instructions',
            'page_loading',
        ]),
        locations_shown(){
            if(this.blood_draw_data!=null){
                if((this.blood_draw_data['in_office']==null || this.blood_draw_data['in_office'].length == 0) && (this.blood_draw_data['draw_site']==null || this.blood_draw_data['draw_site'].length == 0) && (this.blood_draw_data['mobile']==null || this.blood_draw_data['mobile'].length == 0)){
                    // window.location.href = 'https://www.vibrant-america.com/blood-draw-maps/v2';
                    return [];
                }else if(this.active_tab == 'Blood Draw Site'){
                    const idx_of_in_office = this.blood_draw_data.tabs_order.indexOf('in_office');
                    const idx_of_draw_site = this.blood_draw_data.tabs_order.indexOf('draw_site');
                    const modified_in_office_locations=this.blood_draw_data['in_office'].map((item)=>({
                        ...item,
                        tab_type:'in_office',
                    }));
                    const modified_draw_site_locations=this.blood_draw_data['draw_site'].map((item)=>({
                        ...item,
                        tab_type:'draw_site',
                    }));
                    const output = idx_of_in_office < idx_of_draw_site ?[...modified_in_office_locations, ...modified_draw_site_locations]:[...modified_draw_site_locations,...modified_in_office_locations];
                    return output;
                }else if(this.active_tab == 'At-Home Blood Draw'){
                    return this.blood_draw_data['mobile'];
                }
            }
            return [];
        }
    },
    watch:{
        active_tab(newVal){
            console.log('active_tab change', newVal);
            // init selected_location_unit_index to 0 -> default select first location
            this.selected_location_unit_index = 0;
            this.setCurActiveTab(newVal);
            // select first location card

        },
        locations_shown(newVal){
            console.log('location_shown change', newVal);
            if(newVal && newVal.length!=0){
                setTimeout(this.adjustLocationWrappersWhenAllLocationUnitsOverflow, 10);
            }
        }
    },
    methods:{
        ...mapActions(()=>useBloodDrawStore(),[
            'initiateBloodDrawSitePage',
            'storeAllQueryInfomation',
            'setCurActiveTab',
            'getCollectionInstructions',
        ]),
        ...mapActions(()=>useCommonStore(),[
            'setAccessionId',
            'setSampleId',
            'setJwtToken'
        ]),
        selectNewLocationUnit(cur_selected_index){
            //dehighlight the previously selected location unit
            const matched_unit = this.$refs[`location-unit-${this.selected_location_unit_index}`];
            console.log('matched_unit',matched_unit);
            if(matched_unit){
                // console.log('matched_unit');
                // console.dir(matched_unit);
                matched_unit[0].$data.selected = false;
            }
            this.selected_location_unit_index = cur_selected_index;
        },
        goToMapSearch(){
            const redirect_url = this.from_source == 'email' ? `https://phleb.vibrant-wellness.com/blood-draw-maps/v2?sId=${this.sample_id}&accessionId=${this.accession_id}&pId=${this.patient_id}&referBy=conciergeEmail` : 'https://phleb.vibrant-wellness.com/blood-draw-maps/v2';
            window.open(redirect_url, '_blank');
        },
        async goToCollections(){
            this.bloodDrawStore.page_loading = true;
            try{
                let instructions = this.collection_page_instructions;
                if(!instructions){
                    instructions = await this.getCollectionInstructions();
                }
                this.$router.push({
                    name: 'collections',
                    query:{
                        instructions: instructions,
                        accession_id: this.accession_id,
                        has_blood_draw: 'true'
                    }
                });
            }catch(err){
                console.log('goToCollections error:', err);
                ElNotification({
                    title: 'Failed',
                    type: 'error',
                    customClass: "errorNotification",
                    message: err?.message ?? 'Failed to go back to collections page.',
                    duration: 0,
                });
            }finally{
                this.bloodDrawStore.page_loading = false;
            }
        },
        adjustLocationWrappersWhenAllLocationUnitsOverflow(){
            // console.log('checkAllLocationUnitsOverflow',this.$refs['all-location-units'].clientHeight, this.$refs['all-location-units'].scrollHeight);
            const checkedTarget =this.$refs['all-location-units'];
            if(checkedTarget ){
                if(checkedTarget.clientHeight < checkedTarget.scrollHeight){
                    if(window.innerWidth > 500){
                        checkedTarget.style.setProperty('padding-right','16px');
                    }else{
                        checkedTarget.style.setProperty('padding-right','8px');
                    }
                }else{
                    checkedTarget.style.setProperty('padding-right','0px');
                }
            }
        }
    }
}

</script>

<style scoped>
#blood-draw-view-container{
    padding-left: 100px;
    padding-right: 100px;
    margin-top: 30px;

    /*need to explicitly set parent div for children div height to be dynamic*/ 
    height:calc(100vh - 92px);
}

.back-to-collections-btn{
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: #0E4672;
    font-family: 'Roboto';
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 16px;
    padding: 6px 14px 6px 10px;
    border: 1.5px solid #0E4672;
    border-radius: 20px;
    background: transparent;
    user-select: none;
    transition: background 0.15s, color 0.15s;
}
.back-to-collections-btn:hover{
    background: #0E4672;
    color: #fff;
}
.back-to-collections-btn:hover svg path{
    stroke: #fff;
}

.locations-info-wrapper{
    display: flex;
    align-items:center;
    justify-content: space-between;
    gap:16px;
    /*if window's width < 1260: flex-direction: column*/

    margin-top: 38px;
    height:53%;
}
.all-location-units{
    height: 100%; /* if window's width < 1260: 62%*/
    width:52%; /* if window's width < 1260: 100%*/
    box-sizing: border-box; 
    padding-right: 16px;

    flex-shrink:0;

    overflow: auto;

    display: flex;
    flex-direction: column;
    gap:12px;

    /*if window's width < 1260, map is before all-location-units: order: 1 */
}
.all-location-units::-webkit-scrollbar{
    width:8px;
}
.all-location-units::-webkit-scrollbar-track{
    border-radius: 10px;
    background: #F1F3F4;
}
.all-location-units::-webkit-scrollbar-thumb{
    border-radius: 10px;
    background: #D8D8D8;
}
.map-div{
    width:100%; 
    height: 100%; /*if window's width < 1260: 30% */
    border-radius: 10px;
    /* background-color: rgb(231, 168, 168); */
    flex-shrink:1;
}
.no-locations-notice-wrapper{
    width:100%;
    height:498px;

    display:flex;
    align-items:center;
    justify-content:center;
}
.no-locations-notice{
    width:54%;
    text-align:center;

    color: #0E4672;
    font-family: 'Roboto';
    font-size: 18px;
    font-weight: 400;
    line-height: 24px;

    white-space:pre-wrap;
    word-break: break-word;
}
.no-locations-notice .bold{
    font-weight: 700;
}
.nav-to-new-ops-bar{
    width:100%;
    /* height:197px; */
    margin-top:36px;
    margin-bottom: 106px;
    display: flex;
    justify-content: space-between;
    align-items: stretch;
}
.go-to-map-search-div{
    min-height:100%;
    width:calc(50% - 9.5px);
    display:flex;
    align-items:center;
    justify-content:space-between;
    padding-top:24px;
    padding-bottom:18px;

    position:relative;

    border-radius: 10px;
    background: #FAECE6;
}
.go-to-map-search-div.full-width{
    width:100%;
}
.go-to-map-search-div .guidance-info-and-ops-wrapper{
    height: 100%;
    display: flex;
    flex-direction: column;
    row-gap:20px;

    padding-left:36px;
    padding-right:178px;

    width: 100%;
    position: relative;
}
.guidance-title{
    color: #000;
    font-family: 'Roboto';
    font-size: 18px;
    font-weight: 700;
    line-height: 18px; /* 100% */
}
.guidance-text{
    color: #000;
    font-family: 'Roboto';
    font-size: 16px;
    font-weight: 400;
    line-height: 18px; /* 112.5% */
}
.step-list{
    list-style-type: disc; 
    margin-left:20px;
    margin-top:12px;
}
.step-list>li{
    margin-top:6px;
}
.go-to-map-search-div.full-width .go-to-map-search-btn{
    position:relative !important;
}
.go-to-map-search-btn{
    margin-top:6px;
    width:fit-content;
    display: flex;
    gap:6px;
    align-items: center;
    padding: 9px 18px;
    border-radius: 4px;
    background: #0E4672;

    position:absolute;
    bottom:0;

    color: #FFF;
    font-family: 'Roboto';
    font-size: 16px;
    font-weight: 500;
    line-height: 18px; /* 112.5% */
    cursor:pointer;
}
.go-to-map-search-pic{
    width:124px;
    height:105px;
    position:absolute;
    right:7px;
    bottom:0px;
}
/* if screen is smaller than 1260px */
@media only screen and (max-width: 1260px) {
    .locations-info-wrapper{
        display: flex;
        align-items:center;
        justify-content: space-between;
        gap:16px;
        flex-direction: column;

        margin-top: 38px;
        height:65%;
    }
    .all-location-units{
        height: 53%; 
        width: 100%;
        box-sizing: border-box; 
        padding-right: 16px;

        flex-shrink:0;

        overflow: auto;

        display: flex;
        flex-direction: column;
        gap:12px;
        order:1;
    }
    .map-div{
        height:42%;
        width: 100%;
        border-radius: 10px;
        background-color: rgb(231, 168, 168);
        flex-shrink:1;
    }
    .nav-to-new-ops-bar{
        height:fit-content;
        flex-direction: column;
        gap:12px;
    }
    .go-to-map-search-div{
        width:100%;
        height:fit-content;
        padding:18px 0px;
    }
    .go-to-map-search-div .guidance-info-and-ops-wrapper{
        row-gap: 12px;
    }
    .go-to-map-search-btn{
        position: relative;
    }

}

@media only screen and (max-width: 767px){
    #blood-draw-view-container{
        padding-left: 28px;
        padding-right: 27px;
        margin-top: 40px;

        height:fit-content;
        min-height: calc(100vh - 80px);
    }
    .locations-info-wrapper{
        display: flex;
        align-items:center;
        justify-content: space-between;
        gap:12px;
        flex-direction: column;

        margin-top: 18px;
        /* height:65%; */
    }
    .map-div{
        height:221px;
        width: 100%;
        border-radius: 10px;
        background-color: rgb(231, 168, 168);
        flex-shrink:1;
    }
    .all-location-units{
        height: 300px; 
        width: 100%;
        box-sizing: border-box;

        flex-shrink:0;

        display: flex;
        flex-direction: column;
        gap:12px;
        order:1;
    }
    .no-locations-notice-wrapper{
        height: 400px;
        width:100%;
        padding:80px 0px;

        display:flex;
        align-items:center;
        justify-content:center;
    }
    .no-locations-notice{
        width:100%;
        text-align:center;

        color: #0E4672;
        font-family: 'Roboto';
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;

        white-space:pre-wrap;
        word-break: break-word;
    }
    .no-locations-notice .bold{
        font-weight: 700;
    }
    .go-to-map-search-div .guidance-info-and-ops-wrapper{
        row-gap: 6px;
        padding:0 18px;
    }
    .guidance-title{
        color: #000;
        font-family: 'Roboto';
        font-size: 14px;
        font-weight: 700;
        line-height: 18px; /* 128.571% */ 
    }
    .guidance-text{
        color: #000;
        font-family: 'Roboto';
        font-size: 12px;
        font-weight: 400;
        line-height: 18px; /* 150% */
    }
    .step-list{
        list-style-type: disc; 
        margin-left:15px;
        margin-top:6px;
    }
    .step-list>li{
        margin-top:3px;
    }
    .go-to-map-search-btn{
        padding: 8px 12px;
        color: #FFF;
        font-family: 'Roboto';
        font-size: 12px;
        font-weight: 400;
        line-height: 18px;
    }
    .nav-to-new-icon{
        width: 20px;
        height: 20px;
    }
    .go-to-map-search-pic{
        width:77px;
        height:65px;
        position:absolute;
        right:9px;
        bottom:6px;
    }
}
</style>